import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, isEqual } from 'lodash/lang';
import moment from 'moment/moment';
import printJS from 'print-js';

import {
  isAdjustmentEventType,
  isParentEventType,
  isWithholdingEventType
} from 'components/pages/finance/NewInvoice/services/service';

import { FinLedgerEventApi } from '../../../../../api';
import FinInvoiceApi from '../../../../../api/finance/FinInvoiceApi';
import Input from '../../../../../common/data-entry/Input';
import Checkbox from '../../../../../common/data-entry/InputSelectors/Checkbox';
import useLocalStorage from '../../../../../common/hooks/useLocalStorage';
import NotificationManager from '../../../../../common/notifications/NotificationManager';
import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../constants/dateFormat';
import { SOMETHING_WENT_WRONG } from '../../../../../constants/notificationMessages';
import { onRequestError } from '../../../../../services/handlers';
import { CREDIT_MEMO, DEBIT_MEMO, PARENT, WITHHOLDING } from '../InvoiceTable/TableConstants';
import { InvoiceContext } from '../NewInvoiceContext';

import { AmountsSummary } from './AmountsSummary/AmountsSummary';
import { ChipSection } from './ChipSection/ChipSection';
import { ConfigurationSection } from './ConfigurationSection/ConfigurationSection';
import { CreateInvoiceHeader } from './CreateInvoiceHeader/CreateInvoiceHeader';
import { CreateInvoiceTable } from './CreateInvoiceTable/CreateInvoiceTable';
import { EncounterInvoiceTemplate } from './InvoiceTamplates/EncounterInvoiceTemplate';
import { ViewerSection } from './ViewerSection/ViewerSection';
import {
  CONTACT_NAMES,
  CREATE_EXISTING_INVOICES_ERROR_VALIDATION_MESSAGE,
  CREATE_INVOICE_BILL_TO,
  CREATE_INVOICE_ERROR_VALIDATION_MESSAGE,
  CREATE_INVOICE_PAYEE,
  DRAFT,
  REMIT,
  TERMS
} from './CreateInvoiceConstants';
import {
  allInvoiceItemsFromSameSsu,
  allInvoiceItemsWithSameClient,
  areAnyInvoicedItem,
  createMultiplePDFFiles,
  createPDF,
  downloadFile,
  getBlobContent,
  getEventsForSavePayload,
  getFileForSandingEmail,
  getNInvoiceNumbers,
  mapLedgerEventsToIds,
  normalizeId,
  openSendmailModal,
  parseSerializedObject,
  resolveVersion
} from './CreateInvoiceServices';

import './CreateInvoice.scss';

export const CreateInvoice = () => {
  const {
    createInvoiceItems,
    applyFilters,
    setCreateInvoiceItems,
    loadInvoice,
    editPDFMode,
    setEditPDFMode,
    setIncludeAdjustments,
    includeAdjustments,
    invoiceDate
  } = useContext(InvoiceContext);
  const reportTemplateRef = useRef(null);
  const reportTemplateRefWH = useRef(null);
  const creditMemoTemplateRefs = useRef(null);
  const debitMemoTemplateRefs = useRef(null);

  const [selectedPayee, setSelectedPayee] = useState(null);
  const [payees, setPayees] = useState([]);
  const [remit, setRemit] = useState(REMIT.default);
  const [billToList, setBillToList] = useState([]);
  const [selectedBillTo, setSelectedBillTo] = useState(null);
  const [selectedContactName, setSelectedContactName] = useState(CONTACT_NAMES[0]);
  const [selectedInvestigator, setSelectedInvestigator] = useState(null);
  const [investigators, setInvestigators] = useState([]);
  const [selectedTerms, setSelectedTerms] = useState(TERMS[0]);
  const [comment, setComment] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState(DRAFT);
  const [creditMemoInvoiceNumbers, setCreditMemoInvoiceNumbers] = useState([]);
  const [debitMemoInvoiceNumbers, setDebitMemoInvoiceNumbers] = useState([]);
  const [savingType, setSavingType] = useState(null);
  const [recipientEmails, setRecipientEmails] = useState('');

  const [PDFFile, setPDFFile] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalPagesWH, setTotalPagesWH] = useState(0);
  const [totalPagesCM, setTotalPagesCM] = useState({});
  const [totalPagesDM, setTotalPagesDM] = useState({});

  const [currentTab, setCurrentTab] = useState(0);

  const [generationInProgress, setGenerationInProgress] = useState(false);

  const [billToLocalStorage, setBillToLocalStorage] = useLocalStorage(CREATE_INVOICE_BILL_TO, {});
  const [payeeLocalStorage, setPayeeLocalStorage] = useLocalStorage(CREATE_INVOICE_PAYEE, {});

  const generateOriginalPDF = useMemo(
    () => !isEmpty(createInvoiceItems.originalLedgerEvents) && isEmpty(createInvoiceItems.withholdingLedgerEvents),
    [createInvoiceItems.originalLedgerEvents, createInvoiceItems.withholdingLedgerEvents]
  );
  const generateWithholdingPDF = useMemo(
    () => isEmpty(createInvoiceItems.originalLedgerEvents) && !isEmpty(createInvoiceItems.withholdingLedgerEvents),
    [createInvoiceItems.originalLedgerEvents, createInvoiceItems.withholdingLedgerEvents]
  );
  const generateCreditMemoPDF = useMemo(
    () => isEmpty(createInvoiceItems.originalLedgerEvents) && !isEmpty(createInvoiceItems.creditMemoLedgerEvents),
    [createInvoiceItems.originalLedgerEvents, createInvoiceItems.creditMemoLedgerEvents]
  );
  const generateDebitMemoPDF = useMemo(
    () => isEmpty(createInvoiceItems.originalLedgerEvents) && !isEmpty(createInvoiceItems.debitMemoLedgerEvents),
    [createInvoiceItems.originalLedgerEvents, createInvoiceItems.debitMemoLedgerEvents]
  );
  const generateMultiplePDFs = useMemo(
    () => !isEmpty(createInvoiceItems.originalLedgerEvents) && !isEmpty(createInvoiceItems.withholdingLedgerEvents),
    [createInvoiceItems.originalLedgerEvents, createInvoiceItems.withholdingLedgerEvents]
  );

  const mainLedgerEvents = useMemo(() => {
    if (generateWithholdingPDF) return createInvoiceItems.withholdingLedgerEvents;
    if (generateCreditMemoPDF) return createInvoiceItems.creditMemoLedgerEvents;
    if (generateDebitMemoPDF) return createInvoiceItems.debitMemoLedgerEvents;
    return createInvoiceItems.originalLedgerEvents;
  }, [
    createInvoiceItems.creditMemoLedgerEvents,
    createInvoiceItems.debitMemoLedgerEvents,
    createInvoiceItems.originalLedgerEvents,
    createInvoiceItems.withholdingLedgerEvents,
    generateCreditMemoPDF,
    generateDebitMemoPDF,
    generateWithholdingPDF
  ]);
  const parentLedgerEventsWithAdjustments = useMemo(() => {
    const events = [
      ...(createInvoiceItems.originalLedgerEvents ?? []),
      ...(createInvoiceItems.creditMemoLedgerEvents ?? []),
      ...(createInvoiceItems.debitMemoLedgerEvents ?? [])
    ];

    return events.sort((firstEvent, secondEvent) => {
      if (firstEvent.eventNumber === secondEvent.eventNumber) {
        return firstEvent.adjustmentSequence - secondEvent.adjustmentSequence;
      }

      return firstEvent.eventNumber - secondEvent.eventNumber;
    });
  }, [
    createInvoiceItems.originalLedgerEvents,
    createInvoiceItems.creditMemoLedgerEvents,
    createInvoiceItems.debitMemoLedgerEvents
  ]);
  const parentInvoiceDisplayLedgerEvents = useMemo(
    () => (includeAdjustments ? parentLedgerEventsWithAdjustments : createInvoiceItems.originalLedgerEvents),
    [includeAdjustments, parentLedgerEventsWithAdjustments, createInvoiceItems.originalLedgerEvents]
  );

  const investigatorName = useMemo(() => mainLedgerEvents?.[0].investigator, [mainLedgerEvents]);
  const studyName = useMemo(() => mainLedgerEvents?.[0].studyName, [mainLedgerEvents]);
  const pcnName = useMemo(() => mainLedgerEvents?.[0].pcnName, [mainLedgerEvents]);
  const siteName = useMemo(() => mainLedgerEvents?.[0].siteName, [mainLedgerEvents]);
  const siteNumber = useMemo(() => mainLedgerEvents?.[0].siteNumber, [mainLedgerEvents]);
  const studySiteId = useMemo(() => mainLedgerEvents?.[0].ssuId, [mainLedgerEvents]);
  const eventNumber = useMemo(() => mainLedgerEvents?.[0].eventNumber, [mainLedgerEvents]);
  const adjustmentSequence = useMemo(() => mainLedgerEvents?.[0].adjustmentSequence, [mainLedgerEvents]);
  const fmsStudy = useMemo(() => /\bFMS\w*/g.test(pcnName), [pcnName]);

  const invoiceNumberForDisplay = useMemo(() => {
    if (generateWithholdingPDF || createInvoiceItems?.createdInvoiceDetails?.invoiceType === WITHHOLDING)
      return `${createInvoiceItems?.createdInvoiceDetails?.displayNumber ?? invoiceNumber}-WH`;
    if (generateCreditMemoPDF)
      return `${
        mainLedgerEvents[0].invoiceNumber
          ? mainLedgerEvents[0].invoiceNumber + '-' + mainLedgerEvents[0].adjustmentSequence
          : invoiceNumber
      }-CM`;
    if (generateDebitMemoPDF)
      return `${
        mainLedgerEvents[0].invoiceNumber
          ? mainLedgerEvents[0].invoiceNumber + '-' + mainLedgerEvents[0].adjustmentSequence
          : invoiceNumber
      }-DM`;
    if (createInvoiceItems?.createdInvoiceDetails?.invoiceType === CREDIT_MEMO)
      return `${createInvoiceItems?.createdInvoiceDetails?.displayNumber ?? invoiceNumber}-CM`;
    if (createInvoiceItems?.createdInvoiceDetails?.invoiceType === DEBIT_MEMO)
      return `${createInvoiceItems?.createdInvoiceDetails?.displayNumber ?? invoiceNumber}-DM`;
    return createInvoiceItems?.createdInvoiceDetails?.displayNumber ?? invoiceNumber;
  }, [
    createInvoiceItems?.createdInvoiceDetails?.displayNumber,
    createInvoiceItems?.createdInvoiceDetails?.invoiceType,
    generateCreditMemoPDF,
    generateDebitMemoPDF,
    generateWithholdingPDF,
    invoiceNumber,
    mainLedgerEvents
  ]);
  const invoiceCreated = useMemo(() => !isEmpty(createInvoiceItems.createdInvoiceDetails), [
    createInvoiceItems.createdInvoiceDetails
  ]);

  const initialSubject = useMemo(() => `Invoice #${pcnName}-${invoiceNumberForDisplay}`, [
    pcnName,
    invoiceNumberForDisplay
  ]);
  const initialMailText = useMemo(
    () =>
      `<p class="editor-paragraph" dir="ltr"><span style="white-space: pre-wrap;">Hello,</span></p><p class="editor-paragraph" dir="ltr"><br></p><p class="editor-paragraph" dir="ltr"><span style="white-space: pre-wrap;">Please find Invoice #${pcnName}-${invoiceNumberForDisplay} attached. For any questions, please contact ${remit}. We appreciate your business.</span></p><p class="editor-paragraph" dir="ltr"><br></p><p class="editor-paragraph" dir="ltr"><span style="white-space: pre-wrap;">Best,</span></p><p class="editor-paragraph" dir="ltr"><span style="white-space: pre-wrap;">The Elligo Health Research Finance Team</span></p>`,
    [pcnName, invoiceNumberForDisplay, remit]
  );
  const emailModalBoxHeader = useMemo(() => `New Message - Invoice #${pcnName}-${invoiceNumberForDisplay}`, [
    pcnName,
    invoiceNumberForDisplay
  ]);

  const balanceDue = useMemo(
    () => createInvoiceItems.originalLedgerEvents?.reduce((sum, el) => el.invoiceAmount + sum, 0),
    [createInvoiceItems.originalLedgerEvents]
  );

  const paymentsCredits = useMemo(() => {
    const events = [];

    if (!isEmpty(createInvoiceItems.originalLedgerEvents)) {
      events.push(...createInvoiceItems.originalLedgerEvents);
    }
    if (!isEmpty(createInvoiceItems.creditMemoLedgerEvents)) {
      events.push(...createInvoiceItems.creditMemoLedgerEvents);
    }
    if (!isEmpty(createInvoiceItems.debitMemoLedgerEvents)) {
      events.push(...createInvoiceItems.debitMemoLedgerEvents);
    }
    if (!isEmpty(createInvoiceItems.withholdingLedgerEvents)) {
      events.push(...createInvoiceItems.withholdingLedgerEvents);
    }

    return events?.reduce((sum, item) => {
      if (isParentEventType(item.invoiceType)) {
        return sum + item.reconcileTotalAmountPaid;
      }
      if (isAdjustmentEventType(item.invoiceType)) {
        return sum + item.reconcileTotalAmountPaid;
      }

      if (isWithholdingEventType(item.invoiceType)) {
        return sum + item.reconcileTotalAmountPaid;
      }

      return sum;
    }, 0);
  }, [
    createInvoiceItems.originalLedgerEvents,
    createInvoiceItems.creditMemoLedgerEvents,
    createInvoiceItems.debitMemoLedgerEvents,
    createInvoiceItems.withholdingLedgerEvents
  ]);

  const balanceDueWH = useMemo(
    () => createInvoiceItems.withholdingLedgerEvents?.reduce((sum, el) => el.invoiceAmount + sum, 0),
    [createInvoiceItems.withholdingLedgerEvents]
  );
  const currentInvoiceType = useMemo(() => {
    if (generateWithholdingPDF || createInvoiceItems?.createdInvoiceDetails?.invoiceType === WITHHOLDING)
      return WITHHOLDING;
    if (generateCreditMemoPDF || createInvoiceItems?.createdInvoiceDetails?.invoiceType === CREDIT_MEMO)
      return CREDIT_MEMO;
    if (generateDebitMemoPDF || createInvoiceItems?.createdInvoiceDetails?.invoiceType === DEBIT_MEMO)
      return DEBIT_MEMO;
    return PARENT;
  }, [
    createInvoiceItems?.createdInvoiceDetails?.invoiceType,
    generateCreditMemoPDF,
    generateDebitMemoPDF,
    generateWithholdingPDF
  ]);
  const hasParentAdjustments = useMemo(
    () =>
      currentInvoiceType === PARENT &&
      (!isEmpty(createInvoiceItems.creditMemoLedgerEvents) || !isEmpty(createInvoiceItems.debitMemoLedgerEvents)),
    [createInvoiceItems.creditMemoLedgerEvents, createInvoiceItems.debitMemoLedgerEvents, currentInvoiceType]
  );

  useEffect(() => {
    if (createInvoiceItems?.budgetInvoiceDetails?.note && !comment) {
      setComment(createInvoiceItems.budgetInvoiceDetails.note);
    }

    const getEmails = emails => emails?.split(',').map(email => email.trim()) || [];

    const emails = createInvoiceItems?.budgetInvoiceDetails
      ? getEmails(createInvoiceItems?.budgetInvoiceDetails?.emails)
      : getEmails(createInvoiceItems?.createdInvoiceDetails?.recipientEmails);

    setRecipientEmails(emails);
  }, [
    comment,
    createInvoiceItems?.budgetInvoiceDetails,
    createInvoiceItems?.budgetInvoiceDetails?.note,
    createInvoiceItems?.createdInvoiceDetails?.recipientEmails
  ]);

  const emailParams = useMemo(
    () => ({
      currentInvoiceType,
      invoiceNumber,
      initialSubject,
      initialMailText,
      emailTo: selectedBillTo?.email ?? [],
      clientName: selectedBillTo?.name ?? '',
      emailFrom: selectedPayee?.email ?? '',
      emailCc: recipientEmails,
      emailBcc: '',
      emailModalBoxHeader,
      billToList
    }),
    [
      currentInvoiceType,
      invoiceNumber,
      initialSubject,
      initialMailText,
      selectedBillTo?.email,
      selectedBillTo?.name,
      selectedPayee?.email,
      recipientEmails,
      emailModalBoxHeader,
      billToList
    ]
  );

  const generateFileName = useCallback(
    (
      invoiceType,
      number = eventNumber,
      eventSequence = adjustmentSequence,
      invoiceDate = new Date(),
      invoiceVersion = null
    ) => {
      switch (invoiceType) {
        case CREDIT_MEMO:
        case DEBIT_MEMO: {
          let suffix = '';
          if (currentInvoiceType === PARENT) {
            suffix = invoiceType === CREDIT_MEMO ? `-${eventSequence}-CM` : `-${eventSequence}-DM`;
          }
          const eventSuffix = invoiceType === CREDIT_MEMO ? '-CM' : '-DM';
          return `${pcnName}_${siteName}_${number}.${eventSequence}${eventSuffix}_${invoiceNumberForDisplay}${suffix}_${moment()
            .format('DD-MMM-YYYY')
            .toLowerCase()}`;
        }
        default: {
          const withholdingSuffix = invoiceType === WITHHOLDING && currentInvoiceType === PARENT ? '-WH' : '';
          return `${pcnName}_${siteName}_${invoiceNumberForDisplay}${
            invoiceVersion != null ? resolveVersion(Number(invoiceVersion) + 1) : ''
          }${withholdingSuffix}_${moment(invoiceDate)
            .format('DD-MMM-YYYY')
            .toLowerCase()}`;
        }
      }
    },
    [adjustmentSequence, currentInvoiceType, eventNumber, invoiceNumberForDisplay, pcnName, siteName]
  );

  const getMapCreditMemo = () => {
    if (!creditMemoTemplateRefs.current) {
      creditMemoTemplateRefs.current = new Map();
    }
    return creditMemoTemplateRefs.current;
  };

  const getMapDebitMemo = () => {
    if (!debitMemoTemplateRefs.current) {
      debitMemoTemplateRefs.current = new Map();
    }
    return debitMemoTemplateRefs.current;
  };

  const generatePDF = useCallback(async () => {
    const originalFile = isEmpty(createInvoiceItems.originalLedgerEvents)
      ? null
      : await createPDF(reportTemplateRef.current);
    const withholdingFile = isEmpty(createInvoiceItems.withholdingLedgerEvents)
      ? null
      : await createPDF(reportTemplateRefWH.current);
    const creditMemoFiles = !generateCreditMemoPDF ? null : await createMultiplePDFFiles(getMapCreditMemo());
    const debitMemoFiles = !generateDebitMemoPDF ? null : await createMultiplePDFFiles(getMapDebitMemo());
    return Promise.all([originalFile, withholdingFile, creditMemoFiles, debitMemoFiles]);
  }, [
    createInvoiceItems.originalLedgerEvents,
    createInvoiceItems.withholdingLedgerEvents,
    generateCreditMemoPDF,
    generateDebitMemoPDF
  ]);

  const downloadFileAndLog = useCallback(
    fileURL => {
      downloadFile(fileURL, createInvoiceItems?.fileName ?? `${generateFileName(currentInvoiceType)}.pdf`);
      FinInvoiceApi.saveInvoiceLog(invoiceNumber, null, 'DOWNLOAD', currentInvoiceType).then(() => {}, onRequestError);
    },
    [createInvoiceItems?.fileName, generateFileName, currentInvoiceType, invoiceNumber]
  );

  const cancelEmailSanding = useCallback(() => {
    setInvoiceNumber(DRAFT);
    setSavingType(null);
  }, []);

  const handlePrint = useCallback(
    file => {
      printJS({ printable: file.split(',')[1], type: 'pdf', base64: true });
      FinInvoiceApi.saveInvoiceLog(invoiceNumber, null, 'PRINT', currentInvoiceType).then(() => {}, onRequestError);
    },
    [currentInvoiceType, invoiceNumber]
  );

  const onCloseCreateInvoice = useCallback(() => {
    setCreateInvoiceItems({});
    setBillToList([]);
    setCurrentTab(0);
    setGenerationInProgress(false);
    setEditPDFMode(false);
    setIncludeAdjustments(true);
  }, [
    setCreateInvoiceItems,
    setBillToList,
    setCurrentTab,
    setGenerationInProgress,
    setEditPDFMode,
    setIncludeAdjustments
  ]);

  const closeCreateInvoiceAfterSave = useCallback(() => {
    applyFilters();
    onCloseCreateInvoice();
    const savedBillToForCurrentSSU = billToLocalStorage[createInvoiceItems.originalLedgerEvents?.[0].ssuId];
    const savedPayeeForCurrentSSU = payeeLocalStorage[createInvoiceItems.originalLedgerEvents?.[0].ssuId];
    if (!isEqual(savedBillToForCurrentSSU, selectedBillTo.name) && !editPDFMode) {
      setBillToLocalStorage({
        ...billToLocalStorage,
        [createInvoiceItems.originalLedgerEvents?.[0].ssuId]: selectedBillTo.name
      });
    }
    if (!isEqual(savedPayeeForCurrentSSU, selectedPayee) && !editPDFMode) {
      setPayeeLocalStorage({
        ...payeeLocalStorage,
        [createInvoiceItems.originalLedgerEvents?.[0].ssuId]: selectedPayee
      });
    }
  }, [
    applyFilters,
    billToLocalStorage,
    createInvoiceItems.originalLedgerEvents,
    onCloseCreateInvoice,
    payeeLocalStorage,
    selectedBillTo?.name,
    selectedPayee,
    setBillToLocalStorage,
    setPayeeLocalStorage,
    editPDFMode
  ]);

  const resolveActionAfterSave = useCallback(
    (type, invoiceNumber, mainFile) => {
      switch (type) {
        case 'SEND': {
          break;
        }
        case 'PRINT': {
          NotificationManager.success(
            `Invoice #${invoiceNumberForDisplay} was created and printed for the selected Event IDs.`
          );
          handlePrint(mainFile);
          closeCreateInvoiceAfterSave();
          break;
        }
        case 'DOWNLOAD': {
          NotificationManager.success(
            `Invoice #${invoiceNumberForDisplay} was created and downloaded for the selected Event IDs.`
          );
          downloadFileAndLog(mainFile);
          closeCreateInvoiceAfterSave();
          break;
        }
        default: {
          const message = editPDFMode
            ? `Invoice #${invoiceNumberForDisplay} has been successfully updated`
            : `Invoice #${invoiceNumberForDisplay} was created for the selected Event IDs.`;
          closeCreateInvoiceAfterSave();
          NotificationManager.success(message);
          break;
        }
      }
      if (generateMultiplePDFs) {
        if (!isEmpty(createInvoiceItems.withholdingLedgerEvents)) {
          FinInvoiceApi.saveInvoiceLog(invoiceNumber, null, 'SAVE', WITHHOLDING).then(() => {}, onRequestError);
        }
        if (!isEmpty(createInvoiceItems.debitMemoLedgerEvents)) {
          debitMemoInvoiceNumbers.forEach(number =>
            FinInvoiceApi.saveInvoiceLog(number, null, 'SAVE', DEBIT_MEMO).then(() => {}, onRequestError)
          );
        }
        if (!isEmpty(createInvoiceItems.creditMemoLedgerEvents)) {
          creditMemoInvoiceNumbers.forEach(number =>
            FinInvoiceApi.saveInvoiceLog(number, null, 'SAVE', CREDIT_MEMO).then(() => {}, onRequestError)
          );
        }
      }
    },
    [
      closeCreateInvoiceAfterSave,
      createInvoiceItems.creditMemoLedgerEvents,
      createInvoiceItems.debitMemoLedgerEvents,
      createInvoiceItems.withholdingLedgerEvents,
      creditMemoInvoiceNumbers,
      debitMemoInvoiceNumbers,
      downloadFileAndLog,
      editPDFMode,
      generateMultiplePDFs,
      handlePrint,
      invoiceNumberForDisplay
    ]
  );

  const getMainFile = useCallback(
    ({ originalFile, withholdingFile, creditMemoFiles, debitMemoFiles }) => {
      if (generateWithholdingPDF) return withholdingFile;
      if (generateCreditMemoPDF) return creditMemoFiles[0].content;
      if (generateDebitMemoPDF) return debitMemoFiles[0].content;
      return originalFile;
    },
    [generateCreditMemoPDF, generateDebitMemoPDF, generateWithholdingPDF]
  );

  const saveNewInvoice = useCallback(
    (originalFile, withholdingFile, creditMemoFiles, debitMemoFiles) => {
      const resolveEndpointForSave = data => {
        return editPDFMode ? FinLedgerEventApi.editInvoice(data) : FinLedgerEventApi.createInvoice(data);
      };
      const eventsForSavePayload = getEventsForSavePayload({
        currentInvoiceType,
        mainLedgerEvents,
        originalLedgerEvents: createInvoiceItems.originalLedgerEvents,
        creditMemoLedgerEvents: createInvoiceItems.creditMemoLedgerEvents,
        debitMemoLedgerEvents: createInvoiceItems.debitMemoLedgerEvents
      });
      const ledgerEventIds = mapLedgerEventsToIds(eventsForSavePayload);

      const createdInvoiceDetails = {
        client: JSON.stringify(normalizeId(selectedBillTo)),
        terms: selectedTerms.name,
        remit,
        contactName: selectedContactName.name,
        investigator: selectedInvestigator?.name,
        comment,
        payee: JSON.stringify(normalizeId(selectedPayee)),
        invoiceType: currentInvoiceType,
        displayNumber: invoiceNumber,
        invoiceDate: editPDFMode ? invoiceDate : null
      };

      const invoiceFile = originalFile
        ? {
            content: getBlobContent(originalFile),
            fileName: editPDFMode
              ? `${generateFileName(
                  PARENT,
                  null,
                  null,
                  createInvoiceItems?.createdInvoiceDetails?.invoiceCreationDate,
                  createInvoiceItems?.createdInvoiceDetails?.currentVersion
                )}.pdf`
              : `${generateFileName(PARENT)}.pdf`,
            invoiceType: PARENT,
            invoiceNumber
          }
        : null;
      const invoiceWithholdingFile = withholdingFile
        ? {
            content: getBlobContent(withholdingFile),
            fileName: `${generateFileName(WITHHOLDING)}.pdf`,
            invoiceType: WITHHOLDING,
            invoiceNumber
          }
        : null;

      const invoiceCreditMemoFiles = creditMemoFiles
        ? creditMemoFiles.map(({ content, eventId }, index) => ({
            content: getBlobContent(content),
            fileName: `${generateFileName(
              CREDIT_MEMO,
              createInvoiceItems?.creditMemoLedgerEvents?.find(ledgerEvent => ledgerEvent.id === eventId)?.eventNumber,
              createInvoiceItems?.creditMemoLedgerEvents?.find(ledgerEvent => ledgerEvent.id === eventId)
                ?.adjustmentSequence
            )}.pdf`,
            eventId,
            invoiceType: CREDIT_MEMO,
            invoiceNumber: creditMemoInvoiceNumbers[index] ?? invoiceNumber,
            adjustmentDate: createInvoiceItems?.creditMemoLedgerEvents?.find(ledgerEvent => ledgerEvent.id === eventId)
              ?.adjustmentDate
          }))
        : null;

      const invoiceDebitMemoFiles = debitMemoFiles
        ? debitMemoFiles.map(({ content, eventId }, index) => ({
            content: getBlobContent(content),
            fileName: `${generateFileName(
              DEBIT_MEMO,
              createInvoiceItems?.debitMemoLedgerEvents?.find(ledgerEvent => ledgerEvent.id === eventId)?.eventNumber,
              createInvoiceItems?.debitMemoLedgerEvents?.find(ledgerEvent => ledgerEvent.id === eventId)
                ?.adjustmentSequence
            )}.pdf`,
            eventId,
            invoiceType: DEBIT_MEMO,
            invoiceNumber: debitMemoInvoiceNumbers[index] ?? invoiceNumber,
            adjustmentDate: createInvoiceItems?.debitMemoLedgerEvents?.find(ledgerEvent => ledgerEvent.id === eventId)
              ?.adjustmentDate
          }))
        : null;

      return resolveEndpointForSave({
        ledgerEventIds,
        createdInvoiceDetails,
        invoiceFile,
        invoiceWithholdingFile,
        invoiceCreditMemoFiles,
        invoiceDebitMemoFiles
      }).then(response => {
        if (savingType === 'SAVE') {
          const logTypeForSave = editPDFMode ? 'EDIT' : 'SAVE';
          FinInvoiceApi.saveInvoiceLog(invoiceNumber, null, logTypeForSave, currentInvoiceType).then(() => {},
          onRequestError);
        }
        const mainFile = getMainFile({ originalFile, withholdingFile, creditMemoFiles, debitMemoFiles });
        resolveActionAfterSave(savingType, invoiceNumber, mainFile);
        return response;
      }, onRequestError);
    },
    [
      mainLedgerEvents,
      selectedBillTo,
      selectedTerms.name,
      remit,
      selectedContactName.name,
      selectedInvestigator?.name,
      comment,
      selectedPayee,
      currentInvoiceType,
      invoiceNumber,
      editPDFMode,
      invoiceDate,
      generateFileName,
      createInvoiceItems?.createdInvoiceDetails?.invoiceCreationDate,
      createInvoiceItems?.createdInvoiceDetails?.currentVersion,
      createInvoiceItems?.originalLedgerEvents,
      createInvoiceItems?.creditMemoLedgerEvents,
      createInvoiceItems?.debitMemoLedgerEvents,
      creditMemoInvoiceNumbers,
      debitMemoInvoiceNumbers,
      savingType,
      getMainFile,
      resolveActionAfterSave
    ]
  );

  const onSave = useCallback(
    (type = 'SAVE') => {
      if (
        areAnyInvoicedItem(mainLedgerEvents) ||
        editPDFMode ||
        generateWithholdingPDF ||
        generateDebitMemoPDF ||
        generateCreditMemoPDF
      ) {
        if (allInvoiceItemsFromSameSsu(mainLedgerEvents) && allInvoiceItemsWithSameClient(mainLedgerEvents)) {
          if (generateWithholdingPDF) {
            setInvoiceNumber(mainLedgerEvents[0].invoiceNumber);
            setSavingType(type);
          } else if (!editPDFMode || generateCreditMemoPDF || generateDebitMemoPDF) {
            getNInvoiceNumbers(1).then(
              invoiceNumbers => {
                invoiceNumbers.sort((a, b) => a - b);
                setInvoiceNumber(invoiceNumbers[0]);
                const CMNumbers = invoiceNumbers.slice(1, createInvoiceItems.creditMemoLedgerEvents.length + 1);
                const DMNumbers = invoiceNumbers.slice(createInvoiceItems.creditMemoLedgerEvents.length + 1);
                setCreditMemoInvoiceNumbers(CMNumbers);
                setDebitMemoInvoiceNumbers(DMNumbers);
                setSavingType(type);
              },
              () => {
                NotificationManager.error(SOMETHING_WENT_WRONG);
              }
            );
          } else {
            setSavingType(type);
          }
        } else {
          NotificationManager.error(CREATE_INVOICE_ERROR_VALIDATION_MESSAGE);
        }
      } else {
        NotificationManager.error(CREATE_EXISTING_INVOICES_ERROR_VALIDATION_MESSAGE);
      }
    },
    [
      createInvoiceItems.creditMemoLedgerEvents?.length,
      editPDFMode,
      generateCreditMemoPDF,
      generateDebitMemoPDF,
      generateWithholdingPDF,
      mainLedgerEvents
    ]
  );

  useEffect(() => {
    if (isEmpty(createInvoiceItems)) {
      setComment('');
      setPDFFile('');
      setRemit(REMIT.default);
      setSelectedContactName(CONTACT_NAMES[0]);
      setInvoiceNumber(DRAFT);
      setSavingType(null);
      setSelectedTerms(TERMS[0]);
    }
  }, [createInvoiceItems]);

  useEffect(() => {
    if (!isEmpty(createInvoiceItems.createdInvoiceDetails) && !isEmpty(createInvoiceItems.pdf)) {
      setComment(createInvoiceItems.createdInvoiceDetails.comment);
      setInvoiceNumber(createInvoiceItems.createdInvoiceDetails.invoiceNumber);
      setPDFFile(createInvoiceItems.pdf);
    }
  }, [createInvoiceItems.createdInvoiceDetails, createInvoiceItems.pdf]);

  useEffect(() => {
    if (
      (createInvoiceItems.originalLedgerEvents ||
        createInvoiceItems.withholdingLedgerEvents ||
        createInvoiceItems.creditMemoLedgerEvents ||
        createInvoiceItems.debitMemoLedgerEvents) &&
      ((isEmpty(createInvoiceItems.createdInvoiceDetails) && isEmpty(createInvoiceItems.pdf)) || editPDFMode)
    ) {
      setGenerationInProgress(true);
      const delayForGeneration = setTimeout(async () => {
        const [originalFile, withholdingFile, creditMemoFiles, debitMemoFiles] = await generatePDF();
        const mainFile = getMainFile({ originalFile, withholdingFile, creditMemoFiles, debitMemoFiles });
        if (invoiceNumber !== DRAFT && savingType) {
          savingType === 'SEND'
            ? await openSendmailModal({
                ...emailParams,
                initialFiles: getFileForSandingEmail(`${generateFileName(currentInvoiceType)}.pdf`, mainFile),
                closeCreateInvoiceAfterSave,
                saveNewInvoice: () => saveNewInvoice(originalFile, withholdingFile, creditMemoFiles, debitMemoFiles),
                cancelEmailSanding,
                loadInvoice,
                createInvoiceItems,
                setInvoiceNumber,
                setSavingType
              })
            : saveNewInvoice(originalFile, withholdingFile, creditMemoFiles, debitMemoFiles);
        } else {
          setGenerationInProgress(false);
          setPDFFile(mainFile);
        }
      }, 500);
      return () => clearTimeout(delayForGeneration);
    }
  }, [
    cancelEmailSanding,
    closeCreateInvoiceAfterSave,
    createInvoiceItems.createdInvoiceDetails,
    createInvoiceItems.originalLedgerEvents,
    createInvoiceItems.pdf,
    createInvoiceItems.withholdingLedgerEvents,
    createInvoiceItems.creditMemoLedgerEvents,
    createInvoiceItems.debitMemoLedgerEvents,
    emailParams,
    generatePDF,
    generateWithholdingPDF,
    invoiceNumber,
    loadInvoice,
    saveNewInvoice,
    savingType,
    getMainFile,
    generateFileName,
    currentInvoiceType,
    createInvoiceItems,
    editPDFMode,
    includeAdjustments
  ]);

  const elligibleAmountForPageInfo = useMemo(() => {
    if ([CREDIT_MEMO, DEBIT_MEMO].includes(currentInvoiceType)) {
      return mainLedgerEvents[0].invoiceAmount;
    }
    return generateWithholdingPDF ? balanceDueWH : balanceDue;
  }, [balanceDue, balanceDueWH, currentInvoiceType, generateWithholdingPDF, mainLedgerEvents]);

  const pdfWasEdited = useMemo(() => {
    const { createdInvoiceDetails } = createInvoiceItems;
    const createdClient = parseSerializedObject(createdInvoiceDetails?.client);
    const createdPayee = parseSerializedObject(createdInvoiceDetails?.payee);
    return (
      editPDFMode &&
      (!isEqual(createdClient, normalizeId(selectedBillTo)) ||
        !isEqual(createdPayee, normalizeId(selectedPayee)) ||
        !isEqual(
          moment(createdInvoiceDetails?.invoiceDate)?.format(DD_SLASH_MMM_SLASH_YYYY),
          invoiceDate?.format(DD_SLASH_MMM_SLASH_YYYY)
        ) ||
        createdInvoiceDetails.terms !== selectedTerms?.name ||
        createdInvoiceDetails.remit !== remit ||
        hasParentAdjustments ||
        createdInvoiceDetails.contactName !== selectedContactName?.name ||
        createdInvoiceDetails.investigator !== (selectedInvestigator?.name ?? null))
    );
  }, [
    createInvoiceItems,
    editPDFMode,
    hasParentAdjustments,
    invoiceDate,
    remit,
    selectedBillTo,
    selectedContactName?.name,
    selectedInvestigator?.name,
    selectedPayee,
    selectedTerms?.name
  ]);

  const invoiceWasReconciled = useMemo(() => {
    return createInvoiceItems?.originalLedgerEvents?.some(item => item?.reconcileAmount !== null);
  }, [createInvoiceItems?.originalLedgerEvents]);

  return (
    <div className="create-invoice-full-screen">
      <div className="invoice-setup">
        <CreateInvoiceHeader
          editPDFMode={editPDFMode}
          selectedPayee={selectedPayee}
          elligibleAmountForPageInfo={elligibleAmountForPageInfo}
          eventsCount={mainLedgerEvents?.length}
        />
        <div className="panel-content">
          <ConfigurationSection
            billToList={billToList}
            selectedBillTo={selectedBillTo}
            setSelectedBillTo={setSelectedBillTo}
            invoiceCreated={invoiceCreated}
            savingType={savingType}
            payees={payees}
            setPayees={setPayees}
            selectedPayee={selectedPayee}
            setSelectedPayee={setSelectedPayee}
            selectedTerms={selectedTerms}
            setSelectedTerms={setSelectedTerms}
            remit={remit}
            setRemit={setRemit}
            selectedContactName={selectedContactName}
            setSelectedContactName={setSelectedContactName}
            investigators={investigators}
            selectedInvestigator={selectedInvestigator}
            setSelectedInvestigator={setSelectedInvestigator}
            billToLocalStorage={billToLocalStorage}
            payeeLocalStorage={payeeLocalStorage}
            mainLedgerEvents={mainLedgerEvents}
            fmsStudy={fmsStudy}
            studySiteId={studySiteId}
            investigatorName={
              invoiceCreated ? createInvoiceItems?.createdInvoiceDetails?.investigator : investigatorName
            }
            setInvestigators={setInvestigators}
            setBillToList={setBillToList}
          />
          <ChipSection
            invoiceNumber={invoiceNumber !== DRAFT ? invoiceNumberForDisplay : invoiceNumber}
            investigatorName={selectedInvestigator?.name?.split(', ').join(' ')}
            studyName={studyName}
            pcnName={pcnName}
            siteName={siteName}
            siteNumber={siteNumber}
            invoiceDate={
              invoiceCreated
                ? moment(invoiceDate).format(DD_SLASH_MMM_SLASH_YYYY)
                : moment().format(DD_SLASH_MMM_SLASH_YYYY)
            }
          />

          {currentInvoiceType === PARENT && (!invoiceCreated || editPDFMode) && (
            <div style={{ width: '100%', padding: '15px 22px 0 22px' }}>
              <Checkbox
                label="Include Adjustment Details"
                onChange={() => setIncludeAdjustments(!includeAdjustments)}
                checked={!!includeAdjustments}
              />
            </div>
          )}

          <div className="events-section">
            <CreateInvoiceTable
              invoiceCreated={invoiceCreated || !!savingType}
              generateWithholdingPDF={generateWithholdingPDF}
              mainLedgerEvents={mainLedgerEvents}
            />
            <AmountsSummary
              balanceDue={balanceDue}
              balanceDueWH={balanceDueWH}
              paymentsCredits={paymentsCredits}
              CMDMTotalAmount={mainLedgerEvents?.[0].invoiceAmount}
              currentInvoiceType={currentInvoiceType}
              generateWithholdingPDF={generateWithholdingPDF}
            />
          </div>
          <div className="comment-section">
            <Input
              className="create-invoice-input"
              label="Comment"
              value={comment}
              maxLength={400}
              onChange={({ target: { value } }) => setComment(value)}
              validate={false}
              disabled={invoiceCreated || !!savingType}
            />
          </div>
        </div>
      </div>
      <ViewerSection
        onCloseCreateInvoice={onCloseCreateInvoice}
        setBillToList={setBillToList}
        setCurrentTab={setCurrentTab}
        setGenerationInProgress={setGenerationInProgress}
        currentTab={currentTab}
        savingType={savingType}
        invoiceNumber={invoiceNumber}
        PDFFile={PDFFile}
        invoiceCreated={invoiceCreated}
        generateFileName={generateFileName}
        selectedBillTo={selectedBillTo}
        selectedPayee={selectedPayee}
        onSave={onSave}
        handlePrint={handlePrint}
        downloadFileAndLog={downloadFileAndLog}
        emailParams={emailParams}
        setInvoiceNumber={setInvoiceNumber}
        generationInProgress={generationInProgress}
        currentInvoiceType={currentInvoiceType}
        pdfWasEdited={pdfWasEdited}
        invoiceNumberForDisplay={invoiceNumberForDisplay}
        billToList={billToList}
        invoiceWasReconciled={invoiceWasReconciled}
      />
      {(generateMultiplePDFs || generateOriginalPDF) && (isEmpty(createInvoiceItems.pdf) || editPDFMode) && (
        <div ref={reportTemplateRef} className="hided-invoice-template">
          <EncounterInvoiceTemplate
            comment={comment}
            invoices={parentInvoiceDisplayLedgerEvents}
            invoiceNumber={invoiceNumber}
            terms={selectedTerms.name}
            payee={selectedPayee}
            billTo={selectedBillTo}
            contact={selectedContactName}
            investigator={selectedInvestigator?.name}
            remit={remit}
            balanceDue={balanceDue}
            paymentsCredits={paymentsCredits}
            totalPages={totalPages}
            setTotalPages={setTotalPages}
            invoiceType={PARENT}
            invoiceDate={editPDFMode ? invoiceDate : null}
          />
        </div>
      )}
      {(generateMultiplePDFs || generateWithholdingPDF) && (
        <div ref={reportTemplateRefWH} className="hided-invoice-template">
          <EncounterInvoiceTemplate
            comment={comment}
            invoices={createInvoiceItems.withholdingLedgerEvents}
            invoiceNumber={`${invoiceNumber}-WH`}
            terms={selectedTerms.name}
            payee={selectedPayee}
            billTo={selectedBillTo}
            contact={selectedContactName}
            investigator={selectedInvestigator?.name}
            remit={remit}
            paymentsCredits={paymentsCredits}
            balanceDue={balanceDueWH}
            totalPages={totalPagesWH}
            setTotalPages={setTotalPagesWH}
            invoiceType={WITHHOLDING}
          />
        </div>
      )}
      {generateCreditMemoPDF && !isEmpty(createInvoiceItems.creditMemoLedgerEvents) && (
        <>
          {createInvoiceItems.creditMemoLedgerEvents.map(ledgerEvent => (
            <div
              key={ledgerEvent.id + ledgerEvent.eventType}
              ref={node => {
                const map = getMapCreditMemo();
                if (node) {
                  map.set(ledgerEvent.id, node);
                } else {
                  map.delete(ledgerEvent.id);
                }
              }}
              className="hided-invoice-template"
            >
              <EncounterInvoiceTemplate
                comment={comment}
                invoices={[ledgerEvent]}
                invoiceNumber={`${
                  generateCreditMemoPDF && invoiceNumber !== DRAFT
                    ? ledgerEvent.invoiceNumber + '-' + ledgerEvent.adjustmentSequence
                    : invoiceNumber + '-' + ledgerEvent.adjustmentSequence
                }-CM`}
                terms={selectedTerms.name}
                payee={selectedPayee}
                billTo={selectedBillTo}
                contact={selectedContactName}
                investigator={selectedInvestigator?.name}
                remit={remit}
                paymentsCredits={paymentsCredits}
                balanceDue={ledgerEvent.invoiceAmount}
                totalPages={totalPagesCM[ledgerEvent.id] ?? 0}
                setTotalPages={pageCount => {
                  if (totalPagesCM[ledgerEvent.id] !== pageCount) {
                    setTotalPagesCM(prevState => ({
                      ...prevState,
                      [ledgerEvent.id]: pageCount
                    }));
                  }
                }}
                invoiceType={CREDIT_MEMO}
                adjustmentDate={ledgerEvent?.adjustmentDate}
              />
            </div>
          ))}
        </>
      )}
      {generateDebitMemoPDF && !isEmpty(createInvoiceItems.debitMemoLedgerEvents) && (
        <>
          {createInvoiceItems.debitMemoLedgerEvents.map(ledgerEvent => (
            <div
              key={ledgerEvent.id + ledgerEvent.eventType}
              ref={node => {
                const map = getMapDebitMemo();
                if (node) {
                  map.set(ledgerEvent.id, node);
                } else {
                  map.delete(ledgerEvent.id);
                }
              }}
              className="hided-invoice-template"
            >
              <EncounterInvoiceTemplate
                comment={comment}
                invoices={[ledgerEvent]}
                invoiceNumber={`${
                  generateDebitMemoPDF && invoiceNumber !== DRAFT
                    ? ledgerEvent.invoiceNumber + '-' + ledgerEvent.adjustmentSequence
                    : invoiceNumber + '-' + ledgerEvent.adjustmentSequence
                }-DM`}
                terms={selectedTerms.name}
                payee={selectedPayee}
                billTo={selectedBillTo}
                contact={selectedContactName}
                investigator={selectedInvestigator?.name}
                remit={remit}
                paymentsCredits={paymentsCredits}
                balanceDue={ledgerEvent.invoiceAmount}
                totalPages={totalPagesDM[ledgerEvent.id] ?? 0}
                setTotalPages={pageCount => {
                  if (totalPagesDM[ledgerEvent.id] !== pageCount) {
                    setTotalPagesDM(prevState => ({
                      ...prevState,
                      [ledgerEvent.id]: pageCount
                    }));
                  }
                }}
                invoiceType={DEBIT_MEMO}
                adjustmentDate={ledgerEvent?.adjustmentDate}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};
